/**
 * The session, from the browser's side — ARCHITECTURE §11 "Authentication".
 *
 * Ported from ipp-platform (apps/web/src/api/fetch-json.ts and auth/session.ts) so both products
 * behave the same. The browser never sees a token: sign-in and sign-out are full-page navigations
 * to the API, which redirects on to Workspace, and every request carries nothing but HttpOnly
 * cookies.
 *
 * Two rules live here and nowhere else. A 401 is retried once, behind a single shared refresh,
 * because the access token expires long before the session does and nobody should notice. And
 * the browser is only ever sent to sign in by `goToSignIn`, which refuses to go round in circles.
 */

/** Where the member is now, in the form the API's `returnUrl` expects. */
function currentReturnUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * One refresh at a time. A screen can have four queries in flight when the token expires; each
 * posting its own refresh would rotate the cookies under the other three.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= fetch("/api/open/refresh", { method: "POST", credentials: "same-origin" })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

let onForbidden: (() => void) | null = null;

/**
 * Called on every 403. A role can be removed while the page is open; telling the app lets it
 * re-ask `/me`, so the controls on screen follow what the API will actually allow.
 */
export function setForbiddenListener(listener: () => void): void {
  onForbidden = listener;
}

/**
 * `fetch` for every API call — the typed client is built on it, and the two raw downloads use it.
 *
 * The request is cloned before the first send because a body can be read only once and the retry
 * needs it again. `/api/open/*` is never retried: a 401 from there is the answer, not a problem.
 */
export async function authFetch(request: Request): Promise<Response> {
  const retry = request.clone();
  let response = await fetch(request);

  if (response.status === 401 && !new URL(request.url).pathname.startsWith("/api/open/")) {
    // One retry only: a 401 straight after a fresh token means the session really has ended.
    if (await refreshSession()) response = await fetch(retry);
    if (response.status === 401) goToSignIn();
  }
  if (response.status === 403) onForbidden?.();
  return response;
}

// ---------------------------------------------------------------- leaving for Workspace

/**
 * Why the browser must not be sent to sign in on its own. Each is a page the member should read
 * first — and the reason the redirect cannot loop.
 *
 * - `auth_error`: the callback failed. A member with a live Workspace session goes straight
 *   through the login page, so a failure that repeats would loop for ever.
 * - `signed_out`: they asked to leave. Sending them back in would undo it.
 * - `looping`: signed in three times in two minutes and still not signed in — the cookies are
 *   being dropped (an http address, a blocked third-party cookie), and another try ends the same.
 */
export type SignInHold = "auth_error" | "signed_out" | "looping";

const ATTEMPTS_KEY = "qbank.signin-attempts";
const ATTEMPT_WINDOW_MS = 2 * 60 * 1000;
const MAX_ATTEMPTS = 3;

/** Storage can be unavailable (a private window, blocked site data); the guard then just forgets. */
function recentAttempts(): number[] {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(ATTEMPTS_KEY) ?? "[]");
    const cutoff = Date.now() - ATTEMPT_WINDOW_MS;
    return Array.isArray(stored)
      ? stored.filter((t): t is number => typeof t === "number" && t > cutoff)
      : [];
  } catch {
    return [];
  }
}

function recordAttempts(attempts: number[]): void {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch {
    // see recentAttempts
  }
}

/** A session that reached the app ends the count; the next expiry starts afresh. */
export function clearSignInAttempts(): void {
  recordAttempts([]);
}

export function signInHold(): SignInHold | null {
  const params = new URLSearchParams(window.location.search);
  if (params.has("auth_error")) return "auth_error";
  if (params.has("signed_out")) return "signed_out";
  if (recentAttempts().length >= MAX_ATTEMPTS) return "looping";
  return null;
}

/**
 * Set once a navigation has been asked for. Two queries can 401 in the same tick and StrictMode
 * runs effects twice; without this the browser is sent away three times and the last return URL
 * computed wins.
 */
let isLeaving = false;

function leaveFor(url: string): void {
  isLeaving = true;
  recordAttempts([...recentAttempts(), Date.now()]);
  window.location.assign(url);
}

/** Sends the browser to sign in, unless something on screen must be read first. True if it went. */
export function goToSignIn(): boolean {
  if (isLeaving) return true;
  if (signInHold() !== null) return false;
  leaveFor(`/api/open/signin?returnUrl=${encodeURIComponent(currentReturnUrl())}`);
  return true;
}

/** The "Sign in" button on a held page: the member asked, so the hold no longer applies. */
export function signInAgain(): void {
  recordAttempts([]);
  leaveFor(`/api/open/signin?returnUrl=${encodeURIComponent("/")}`);
}

/**
 * Signs out of the Question Bank and stays here, on a page that says so.
 *
 * ipp-platform lands on Workspace's login page instead, but that page signs anyone with a live
 * Workspace session straight back through — so "Sign out" would look like it did nothing.
 */
export async function goToSignOut(): Promise<void> {
  if (isLeaving) return;
  isLeaving = true;
  await fetch("/api/open/logout", {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  }).catch(() => undefined);
  window.location.assign("/?signed_out=1");
}

/**
 * Finishes a sign-out Workspace started. `/logout-callback` is the post-logout redirect URI
 * registered with it, and Workspace lands there once its own session is over — signed out there,
 * or expired — which leaves our cookies holding tokens for a session that no longer exists.
 *
 * They are HttpOnly, so clearing them is the API's job. A navigation, not `goToSignOut`'s JSON
 * call: the API then redirects to Workspace's login page, which — Workspace's session being over —
 * asks for a login rather than signing the member straight back through, and comes back to
 * `returnUrl` afterwards. The API clamps that to our own origin, so a crafted link here cannot
 * send anyone elsewhere.
 */
export function completeSignOut(returnUrl: string | null): void {
  if (isLeaving) return;
  isLeaving = true;
  const query = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : "";
  window.location.assign(`/api/open/logout${query}`);
}

// ---------------------------------------------------------------- is the session still live?

interface SessionStatus {
  isLoggedIn: boolean;
  isTokenRevoked: boolean;
}

function isSessionReply(value: unknown): value is { data: SessionStatus } {
  if (!value || typeof value !== "object" || !("data" in value)) return false;
  const { data } = value;
  return (
    !!data &&
    typeof data === "object" &&
    typeof (data as SessionStatus).isLoggedIn === "boolean" &&
    typeof (data as SessionStatus).isTokenRevoked === "boolean"
  );
}

/**
 * The auth server's own verdict, asked from the browser with credentials because it decides from
 * its own cookies on its own domain — the API cannot ask on our behalf (apps/api/auth/oidc.py).
 *
 * Null when there is nothing to ask (`checkUrl` null on localhost). Anything short of a clear
 * answer throws rather than reporting the session as ended: a member is sent away only on the
 * auth server's word, never because a check could not be made.
 */
export async function loadSessionStatus(checkUrl: string | null): Promise<SessionStatus | null> {
  if (checkUrl === null) return null;
  const response = await fetch(checkUrl, { credentials: "include" });
  if (!response.ok) throw new Error(`The auth server answered ${response.status} to the session check`);
  const body: unknown = await response.json();
  if (!isSessionReply(body))
    throw new Error("The auth server's session check came back in an unexpected shape");
  return body.data;
}
